import { useState, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ChevronRight, Download } from 'lucide-react'

const ACTION_META: Record<string, { icon: string; label: string; colour: string }> = {
  event_created:       { icon: '📋', label: 'Event created',          colour: 'text-slate-600' },
  ai_run:              { icon: '🤖', label: 'AI selection computed',  colour: 'text-blue-600'  },
  cco_override:        { icon: '⚠️', label: 'Institution Admin override', colour: 'text-amber-600' },
  cco_add:             { icon: '➕', label: 'Journalist added',          colour: 'text-green-600' },
  codes_generated:     { icon: '🔑', label: 'Codes generated',        colour: 'text-purple-600'},
  attendance_recorded: { icon: '📍', label: 'Attendance recorded',    colour: 'text-green-600' },
  restriction_applied: { icon: '🔒', label: 'Restriction applied',    colour: 'text-red-600'   },
  restriction_lifted:  { icon: '🔓', label: 'Restriction lifted',     colour: 'text-green-600' },
  access_granted:      { icon: '🔓', label: 'Support access granted', colour: 'text-blue-600'  },
  access_revoked:      { icon: '⛔', label: 'Support access revoked', colour: 'text-red-600'   },
}

const PAGE_SIZE = 25

export function AuditLogView() {
  const [orgFilter, setOrgFilter] = useState<string>('all')
  const [actionFilter, setActionFilter] = useState<string>('all')
  const [dateFrom, setDateFrom] = useState('')
  const [page, setPage] = useState(0)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const { data: orgs } = useQuery({
    queryKey: ['audit-orgs-list'],
    queryFn: async () => {
      const { data } = await supabase
        .from('organizations')
        .select('id, name')
        .not('settings->>is_platform_owner', 'eq', 'true')
        .order('name')
      return (data || []) as any[]
    }
  })

  const { data: logs } = useQuery({
    queryKey: ['platform-audit', orgFilter, actionFilter, dateFrom, page],
    queryFn: async () => {
      let query = (supabase
        .from('event_audit_log')
        .select(`
          id, action, payload, created_at,
          events(name, org_id, organizations(name)),
          profiles(full_name)
        `)
        .order('created_at', { ascending: false })
        .range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1) as any)

      if (orgFilter && orgFilter !== 'all') {
        query = query.eq('org_id', orgFilter)
      }
      if (actionFilter && actionFilter !== 'all') {
        query = query.eq('action', actionFilter)
      }
      if (dateFrom) {
        query = query.gte('created_at', new Date(dateFrom).toISOString())
      }

      const { data } = await query
      return (data || []) as any[]
    }
  })

  const exportCSV = () => {
    if (!logs?.length) return
    const headers = ['Timestamp', 'Actor', 'Institution', 'Action', 'Event', 'Payload']
    const rows = logs.map((l: any) => [
      new Date(l.created_at).toISOString(),
      l.profiles?.full_name ?? '',
      l.events?.organizations?.name ?? '',
      ACTION_META[l.action]?.label ?? l.action,
      l.events?.name ?? '',
      JSON.stringify(l.payload ?? {}),
    ])
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `audit-log-${new Date().toISOString().slice(0,10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const reset = () => { setPage(0); setExpandedId(null) }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Platform Audit Log</h2>
          <p className="text-justify text-sm text-muted-foreground mt-0.5">
            All platform events across institutions, in reverse chronological order.
          </p>
        </div>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={exportCSV} disabled={!logs?.length}>
          <Download className="w-3.5 h-3.5" />
          Export CSV
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-36">
              <Select value={orgFilter} onValueChange={v => { setOrgFilter(v); reset() }}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="All institutions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All institutions</SelectItem>
                  {(orgs || []).map((o: any) => (
                    <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-36">
              <Select value={actionFilter} onValueChange={v => { setActionFilter(v); reset() }}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="All actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All actions</SelectItem>
                  {Object.entries(ACTION_META).map(([key, meta]) => (
                    <SelectItem key={key} value={key}>{meta.icon} {meta.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-36">
              <Input
                type="date"
                value={dateFrom}
                onChange={e => { setDateFrom(e.target.value); reset() }}
                className="h-9 text-sm"
                placeholder="From date"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Log table */}
      <Card>
        <CardContent className="p-0">
          {(logs || []).length === 0 ? (
            <div className="p-10 text-center text-muted-foreground text-sm">
              No audit entries match the current filters.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground w-36">Timestamp</th>
                  <th className="px-2 py-2.5 text-left font-medium text-muted-foreground">Actor</th>
                  <th className="px-2 py-2.5 text-left font-medium text-muted-foreground hidden md:table-cell">Institution</th>
                  <th className="px-2 py-2.5 text-left font-medium text-muted-foreground">Action</th>
                  <th className="px-2 py-2.5 text-left font-medium text-muted-foreground hidden lg:table-cell">Event</th>
                  <th className="px-2 py-2.5 w-6" />
                </tr>
              </thead>
              <tbody>
                {(logs || []).map((log: any) => {
                  const meta = ACTION_META[log.action]
                  const isExpanded = expandedId === log.id
                  return (
                    <>
                      <tr
                        key={log.id}
                        className="border-b last:border-0 hover:bg-muted/10 cursor-pointer"
                        onClick={() => setExpandedId(isExpanded ? null : log.id)}
                      >
                        <td className="px-4 py-3 text-muted-foreground text-xs whitespace-nowrap">
                          {new Date(log.created_at).toLocaleString()}
                        </td>
                        <td className="px-2 py-3 font-medium">
                          {log.profiles?.full_name ?? <span className="text-muted-foreground">System</span>}
                        </td>
                        <td className="px-2 py-3 text-muted-foreground hidden md:table-cell">
                          {log.events?.organizations?.name ?? ''}
                        </td>
                        <td className="px-2 py-3">
                          {meta ? (
                            <span className={`flex items-center gap-1.5 ${meta.colour}`}>
                              <span>{meta.icon}</span>
                              <span className="text-sm">{meta.label}</span>
                            </span>
                          ) : (
                            <Badge variant="outline" className="text-xs">{log.action}</Badge>
                          )}
                        </td>
                        <td className="px-2 py-3 text-muted-foreground hidden lg:table-cell text-sm">
                          {log.events?.name ?? ''}
                        </td>
                        <td className="px-2 py-3 text-muted-foreground">
                          <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr key={`${log.id}-expanded`} className="border-b bg-muted/20">
                          <td colSpan={6} className="px-6 py-3">
                            <pre className="text-xs font-mono bg-muted/40 p-3 rounded-lg overflow-x-auto whitespace-pre-wrap break-all">
                              {JSON.stringify(log.payload ?? {}, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </>
                  )
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          size="sm"
          disabled={page === 0}
          onClick={() => setPage(p => Math.max(0, p - 1))}
        >
          Previous
        </Button>
        <span className="text-sm text-muted-foreground">Page {page + 1}</span>
        <Button
          variant="outline"
          size="sm"
          disabled={(logs || []).length < PAGE_SIZE}
          onClick={() => setPage(p => p + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  )
}
